import { Injectable } from "@angular/core";
import { HttpClientService } from "./http-client.service";
import { Observable, of } from "rxjs";
import { HttpResponse } from "@angular/common/http";
import { catchError, map, tap } from "rxjs/operators";

// Default financing services offered when a contact has no service policy.
export const DEFAULT_ALLOWED_SERVICES = ["credit", "leasing", "leasing_residual"];

export interface AllowedServicesFlags {
  allowed_services: string[];
  is_leasing_allowed: boolean;
  is_credit_allowed: boolean;
  is_leasing_residual_allowed: boolean;
}

// Normalize an allowed_services payload into the flags consumed by the quoters,
// falling back to all services when no policy is provided.
export function buildAllowedServicesFlags(services?: string[]): AllowedServicesFlags {
  const allowed_services =
    Array.isArray(services) && services.length > 0 ? services : [...DEFAULT_ALLOWED_SERVICES];
  return {
    allowed_services,
    is_leasing_allowed: allowed_services.includes("leasing"),
    is_credit_allowed: allowed_services.includes("credit"),
    is_leasing_residual_allowed: allowed_services.includes("leasing_residual"),
  };
}

@Injectable()
export class ContactsService {
  constructor(private http: HttpClientService) {}
  public addContact({
    first_name,
    middle_name = null,
    first_last_name = null,
    second_last_name = null,
    phones = [],
    emails = [],
    dealership_id = null,
    source_channel = null,
    bucket_key = null,
  }): Observable<any> {
    const params = {
      first_name,
      middle_name,
      first_last_name,
      second_last_name,
      phones,
      emails,
      dealership_id,
      source_channel,
      bucket_key,
    };
    return this.http.post("/api/contact", params).pipe(tap((_) => console.log("ContactsService.createContact: Ok.")));
  }

  public updateContact(contact, source_channel = null): Observable<any> {
    const options = {
      first_name: contact.first_name,
      middle_name: contact.middle_name,
      first_last_name: contact.first_last_name,
      second_last_name: contact.second_last_name,
      phones: contact.phones,
      emails: contact.emails,
      dealership_id: contact.dealership_id,
      source_channel,
      internal_user_id: contact.internal_user_id,
      external_user_id: contact.external_user_id,
      bucket_key: contact.bucket_key,
    };
    return this.http
      .put(`/api/contact/${contact.id}`, options)
      .pipe(tap((_) => console.log("ContactsService.updateContact: Ok.")));
  }

  public changeAssigned(id: string | number, owner_id: string | number): Observable<any> {
    const params = {
      id,
      owner_id,
    };
    return this.http
      .post(`/api/contact/${id}/assign`, params)
      .pipe(tap((_) => console.log("ContactsService.changeAssigned: Ok.")));
  }

  public toogleActive(id: string | number, reason?: string): Observable<any> {
    const params = {
      id,
      reason,
    };
    return this.http
      .post(`/api/contact/${id}/toogle_active`, params)
      .pipe(tap((_) => console.log("ContactsService.toogleActive: Ok.")));
  }

  public createOpportunity(id: string | number): Observable<any> {
    return this.http
      .post(`/api/contact/${id}/create_opportunity`, {})
      .pipe(tap((_) => console.log("ContactsService.createOpportunity: Ok.")));
  }

  public removeOpportunity(id: string | number): Observable<any> {
    return this.http
      .post(`/api/contact/${id}/remove_opportunity`, {})
      .pipe(tap((_) => console.log("ContactsService.removeOpportunity: Ok.")));
  }

  public getContact(id: string): Observable<any> {
    return this.http
      .get(`/api/contact/${id}/contact_information`)
      .pipe(tap((_) => console.log("ContactsService.getContact: Ok.")));
  }

  public getContacts(
    status = "",
    person_id = "",
    filter = "",
    dateFilters: any[],
    sort_by = "created_at",
    sort_order = "asc",
    page_number = "1",
    page_size = "20",
    view_as: string[],
    dealerships: string[]
  ): Observable<any> {
    const options = {
      params: {
        person_id,
        status,
        filter,
        sort_by,
        sort_order,
        page_number,
        page_size,
        "view_as[]": view_as,
        "dealerships[]": dealerships,
        "date_filters[]": dateFilters.map((i) => JSON.stringify(i)),
      },
    };
    return this.http.get("/api/contact", options).pipe(tap((_) => console.log("ContactsService.getContacts: Ok.")));
  }

  public getGrouped(
    status = "",
    person_id = "",
    filter = "",
    dateFilters: any[],
    sort_by = "created_at",
    sort_order = "asc",
    page_number = "1",
    page_size = "10",
    view_as: string,
    dealerships: string[]
  ): Observable<any> {
    const options = {
      params: {
        person_id,
        status,
        filter,
        sort_by,
        sort_order,
        page_number,
        page_size,
        view_as,
        "dealerships[]": dealerships,
        "date_filters[]": dateFilters.map((i) => JSON.stringify(i)),
      },
    };

    return this.http.get("/api/contact/grouped", options);
  }

  public getPersons(id: string): Observable<any> {
    return this.http.get(`/api/contact/${id}/persons`).pipe(tap((_) => console.log("ContactsService.getPersons: Ok.")));
  }

  public getQuotes(id: string): Observable<any> {
    return this.http.get(`/api/contact/${id}/quotes`).pipe(tap((_) => console.log("ContactsService.getQuotes: Ok.")));
  }

  public addFollowUp(contact_id: string | number, exec_time: Date): Observable<any> {
    const params = {
      contact_id,
      exec_time,
    };
    return this.http.post("/api/follow_ups", params).pipe(tap((_) => console.log("ContactsService.addFollowUp: Ok.")));
  }

  public deleteFollowUp(contact_id: string): Observable<any> {
    return this.http
      .delete(`/api/contact/${contact_id}/follow_ups/destroy`)
      .pipe(tap((_) => console.log("ContactsService.deleteFollowUp: Ok.")));
  }

  public updateEmailsAndPhones(contact_id, emails, phones): Observable<any> {
    const options = {
      emails,
      phones,
    };
    return this.http
      .put(`/api/contact/${contact_id}/update_emails_and_phones`, options)
      .pipe(tap((_) => console.log("ContactsService.updateEmailsAndPhones: Ok.")));
  }

  public assignOnline(id: string | number, media_url: string = undefined): Observable<any> {
    const params = {
      id,
      media_url,
    };
    return this.http
      .post(`/api/contact/${id}/assign_online`, params)
      .pipe(tap((_) => console.log("ContactsService.assignOnline: Ok.")));
  }

  public assignSales(contact_id: number, owner_id: number): Observable<any> {
    const params = {
      id: contact_id,
      owner_id,
    };
    return this.http
      .post(`/api/contact/${contact_id}/assign_sales`, params)
      .pipe(tap((_) => console.log("ContactsService.assignSales: Ok.")));
  }

  public ownersInformation(id: string | number): Observable<any> {
    const params = {
      id,
    };
    return this.http
      .post("/api/contact/owners_information", params)
      .pipe(tap((_) => console.log("ContactsService.ownersInformation: Ok.")));
  }

  public getIndexClientList(params): Observable<any> {
    const options = {
      params,
    };

    return this.http
      .get("/api/contact/index_client_list", options)
      .pipe(tap((_) => console.log("ContactsService.getIndexClientList: Ok.")));
  }

  public getExternalQuotes(contact_id: number): Observable<any> {
    return this.http
      .get(`/api/contact/${contact_id}/external_quotes`)
      .pipe(tap((_) => console.log("ContactsService.getExternalQuotes: Ok.")));
  }

  public updateExternalQuotes(contact_id: number, status: string): Observable<any> {
    const params = {
      first_call_to_client_status: status,
    };
    return this.http
      .put(`/api/contact/${contact_id}/update_external_quotes`, params)
      .pipe(tap((_) => console.log("ContactsService.updateExternalQuotes: Ok.")));
  }

  public getAllowedServices(contact_id: number): Observable<any> {
    return this.http
      .get(`/api/contact/${contact_id}/allowed_services`)
      .pipe(tap((_) => console.log("ContactsService.getAllowedServices: Ok.")));
  }

  // Fetch the contact's allowed services and normalize them into quoter flags,
  // defaulting to all services on empty/invalid response or request error.
  public resolveAllowedServices(contact_id: number | string): Observable<AllowedServicesFlags> {
    return this.getAllowedServices(Number(contact_id)).pipe(
      map((response) => buildAllowedServicesFlags(response?.allowed_services)),
      catchError(() => of(buildAllowedServicesFlags()))
    );
  }
}
